import { TextField, LinearProgress } from "@mui/material";
import { ReactComponent as VideoIcon } from "../../icons/video_icon.svg";
import { ReactComponent as ImageIcon } from "../../icons/image_icon.svg";
import React, { useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { fetchUrl } from "../../../utils/upload";
import { FileUploader } from "react-drag-drop-files";
import { metrics } from "utils/metrics";

import {
  GIF_MIME_TYPE,
  VIDEO_MIME_TYPES,
  IMAGE_MIME_TYPES,
  validateSize,
  validateMimeType
} from "utils/uploadFileValidation";

import { toast } from "react-hot-toast";
import { FileSizeError } from "components/upload/FileSizeError";
import { FileMimeTypeError } from "components/upload/FileMimeTypeError";
import { addFiles } from "utils/uploadAddFile";
import { BaseButton, SECONDARY } from "components/buttons/BaseButton";
import "./styles/UploadSelectGifType.scss";

interface IProps {
  setFileType: (type: string) => void;
  setFile: (file: any) => void;
  fileUrl: string;
  setFileUrl: (url: string) => void;
  setFiles: (files: any) => void;
  setFromUrl: (url: string) => void;
  setTimeLineURL: (url: string) => void;
  setTicketId: (id: any) => void;
  setCurrentStep: (number: number) => void;
}

const videoAcceptTypes = [...VIDEO_MIME_TYPES, GIF_MIME_TYPE];
const imageAcceptTypes = IMAGE_MIME_TYPES;
const BULK_UPLOAD = "BULK_UPLOAD";
const MAX_BULK_IMAGES = 50;

const UploadSelectGifType = (props: IProps) => {
  const {
    setFileType,
    setFile,
    setFileUrl,
    setFromUrl,
    fileUrl,
    setFiles,
    setTimeLineURL,
    setTicketId,
    setCurrentStep
  } = props;

  const [urlLoad, setUrlLoad] = useState(false);
  const [urlError, setUrlError] = useState("");
  const imageInputRef = useRef<any>(null);
  const bulkImagesInputRef = useRef<any>(null);
  const videoInputRef = useRef<any>(null);

  const fetchUrlReq = async (urlVal: string) => {
    setUrlLoad(true);
    setUrlError("");

    try {
      await metrics.sendUploadUrlSelected();

      const { downloadUrl, timelineUrl, kind, id } = await fetchUrl({ url: urlVal });

      setFileUrl(downloadUrl);
      setFileType(kind);
      setFromUrl(downloadUrl);
      setTicketId(id);

      if (kind === "video") {
        setTimeLineURL(timelineUrl || "");
      }
    } catch (err) {
      setUrlError("Error uploading a file.");
    } finally {
      setUrlLoad(false);
    }
  };

  const handlePasteUrl = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasteString = e.clipboardData.getData("text");

    if (pasteString.length) {
      setFileUrl(pasteString);
      fetchUrlReq(pasteString);
    }
  };

  const handleSubmitUrl = (event: React.FormEvent) => {
    event.preventDefault();

    if (fileUrl.length) {
      fetchUrlReq(fileUrl);
    }
  };

  const handleFileUrl = (e: React.ChangeEvent<HTMLInputElement>) => {
    const urlVal = e.target.value;
    setFileUrl(urlVal);
  };

  const handleButtonClick = async (type: string) => {
    if (type === "video") {
      if (videoInputRef && videoInputRef.current) {
        videoInputRef.current.click();
        await metrics.sendUploadVideoSelected();
      }
    } else if (type === "image") {
      if (imageInputRef && imageInputRef.current) {
        imageInputRef.current.click();
        await metrics.sendUploadImageSelected();
      }
    } else if (type === "bulkImages") {
      if (bulkImagesInputRef?.current) {
        bulkImagesInputRef.current.click();
        await metrics.sendUploadBulkClick();
      }
    }
  };

  const handleBulkImages = async (newFiles: FileList) => {
    if (!validateSize(newFiles)) {
      toast.error(FileSizeError, {
        duration: 5000
      });
      return;
    }

    if (newFiles.length > MAX_BULK_IMAGES) {
      toast.error("Should be less than 50 images", {
        duration: 5000
      });
      return;
    }

    setFiles(await addFiles([], newFiles, MAX_BULK_IMAGES));
    await setCurrentStep(4);
  };

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    acceptTypes: string[],
    variant?: string
  ) => {
    const newFiles = event.target.files;
    if (newFiles === null) {
      return;
    }

    if (!validateMimeType(newFiles, acceptTypes)) {
      toast.error(FileMimeTypeError, {
        duration: 5000
      });
      return;
    }

    if (variant === BULK_UPLOAD) {
      handleBulkImages(newFiles);
      return;
    }

    handleFile(newFiles);
  };

  const handleFile = async (newFiles: FileList) => {
    if (!validateSize(newFiles)) {
      toast.error(FileSizeError, {
        duration: 5000
      });
      return;
    }

    if (newFiles.length < 2) {
      const type = validateMimeType(newFiles, VIDEO_MIME_TYPES) ? "video" : "image";
      const [{ url, file }] = await addFiles([], newFiles);
      setFileUrl(url);
      setFile(file);
      setFileType(type);
    } else {
      setFileType("multiple");
      setFiles(await addFiles([], newFiles));
    }
  };

  const handleDropFileChange = async (file: FileList) => {
    const dataTransfer = new DataTransfer();

    for (let i = 0; i < file.length; i++) {
      dataTransfer.items.add(file[i]);
    }

    if (dataTransfer.items.length > 1) {
      if (validateMimeType(dataTransfer.files, IMAGE_MIME_TYPES)) {
        await metrics.sendUploadImagesDropped();
        await handleFile(dataTransfer.files);
      }

      return;
    }

    if (validateMimeType(dataTransfer.files, VIDEO_MIME_TYPES)) {
      await metrics.sendUploadVideoDropped();
      await handleFile(dataTransfer.files);
      return;
    }

    if (validateMimeType(dataTransfer.files, IMAGE_MIME_TYPES)) {
      await metrics.sendUploadImageDropped();
      await handleFile(dataTransfer.files);
      return;
    }

    toast.error(FileMimeTypeError, {
      duration: 5000
    });
  };

  return (
    <div className="UploadSelectGifType">
      <FileUploader
        classes="file-drop"
        handleChange={handleDropFileChange}
        name="file"
        multiple
        disabled
      >
        <div className="UploadSelectGifType-Container">
          <h4>Choose file or define URL</h4>
          <div className="UploadContainer">
            <div className="UploadButtons">
              <BaseButton
                variant={SECONDARY}
                onClick={() => handleButtonClick("video")}
              >
                <VideoIcon />
                <span>Select a Video</span>
              </BaseButton>
              <BaseButton
                variant={SECONDARY}
                onClick={() => handleButtonClick("image")}
              >
                <ImageIcon />
                <span>Select Image(s)</span>
              </BaseButton>
            </div>
            <form onSubmit={handleSubmitUrl}>
              <span className="InputDescription">Create content from any link/URL. Copy and paste below to upload your Image or GIF.</span>
              <TextField
                value={fileUrl}
                onChange={handleFileUrl}
                onPaste={handlePasteUrl}
                placeholder="URL"
                disabled={urlLoad}
                sx={{
                  marginTop: "1rem",
                  width: "100%",
                  background: "transparent",
                  "& label.Mui-focused": {
                    color: "#D8DFEC"
                  },
                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    border: "1px solid rgba(216, 223, 236, 0.3)"
                  },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    color: "white",
                    border: "1px solid rgba(216, 223, 236, 0.3)",
                    height: "50px"
                  },
                  "& .MuiFormHelperText-root.Mui-error": {
                    margin: 0,
                    marginTop: 5,
                  },
                  "& input.Mui-disabled": {
                    color: "white",
                    WebkitTextFillColor: "white"
                  }
                }}
              />
            </form>

            {urlLoad && (
              <>
                <LinearProgress
                  sx={{
                    marginTop: "20px",
                    borderRadius: "5px",
                    ".MuiLinearProgress-bar": {
                      backgroundColor: "#8F2CF2"
                    }
                  }}
                  color="secondary"
                />
                <p className="upload-text">Please wait while we're downloading the video.</p>
              </>
            )}
            {!!urlError && <p className="ErrorMessage">{urlError}</p>}
          </div>
          <span className="SeparateBtn">or</span>
          <div className="QuickUpload">
            <p>
              Bulk upload up to 50 images. Images uploaded via the Bulk Upload option will not feature in
              RedGIFs Scroll Feeds unless (min) 3 tags have been added and the content made public.
            </p>
            <BaseButton
              variant={SECONDARY}
              onClick={() => handleButtonClick("bulkImages")}
            >
              <ImageIcon />
              <span>Bulk Upload Images</span>
            </BaseButton>
          </div>
          <NavLink to="/guidelines">Community Guidelines</NavLink>
        </div>
      </FileUploader>
      <input
        style={{ display: "none" }}
        ref={videoInputRef}
        accept={videoAcceptTypes.join(",")}
        type="file"
        onChange={(e) => handleFileChange(e, videoAcceptTypes)}
      />
      <input
        style={{ display: "none" }}
        ref={imageInputRef}
        multiple
        type="file"
        accept={imageAcceptTypes.join(",")}
        onChange={(e) => handleFileChange(e, imageAcceptTypes)}
      />
      <input
        style={{ display: "none" }}
        ref={bulkImagesInputRef}
        multiple
        type="file"
        accept={imageAcceptTypes.join(",")}
        onChange={(e) => handleFileChange(e, imageAcceptTypes, BULK_UPLOAD)}
      />
    </div>
  );
};

export default UploadSelectGifType;

